import { lifecycle, compose, withHandlers, withProps, branch, renderComponent, withState } from 'recompose'
import { connect } from 'react-redux'
import isEqual from 'lodash/isEqual'
import isEmpty from 'lodash/isEmpty'
import debounce from 'lodash/debounce'
import { show } from 'redux-modal'
import { change } from 'redux-form'
import { rest } from 'services'
import toggle from 'components/common/hoc/toggle'
import withScrollToAnchor from 'components/common/hoc/withScrollToAnchor'
import { getLocationPathname } from 'selectors/getPageLocation'
import { getCurrentStorePhoneNumber, getCurrentStoreNumber } from 'selectors/getCurrentStore'
import { getQuoteTotalCount, getQuoteItemAndServices } from 'selectors/shoppingData'
import { getQuickApptInitialized, getUserData } from 'components/Appointment/selectors'
import { googleAnalyticDatepickerTomorrowSelected, googleAnalyticDatepickerTodaysSelected } from 'actions/googleAnalytic'
import shuffle from 'helpers/shuffle'
import { arrayHasValues } from 'helpers/utils'
import { isMobile } from 'components/common/Media/helpers'
import {
  getIsQuotePage,
  getIsQuoteSummaryPage,
  getIsOnlineAppointment
} from '../../../BOTAppointmentSummary/helpers'
import { getTimeData } from '../../helpers'
import {
  processDate,
  processTime,
  tomorrowSelected,
  wasProductOrServiceAddedOrRemoved,
  generateAvailableApptTimesPayload,
  filterAppointmentSlotsForPilotStore
} from './helpers'
import { TIME_SLOTS_ID, ITEMS_LIMIT, ITEMS_NEXT_DAY_THRESHOLD, TIME_SLOTS_TABLE_ID } from './constants'
import DateTimeSelector from './DateTimeSelector'
import CheckoutDateTimeSelector from './CheckoutDateTimeSelector'
import { validatePromoExpDate } from 'components/ShoppingQuote/BOTProductAndServicesQuoteNew/components/PromotionExpirationModal/actions.js'
import {
  getAppointmentDatesNotInRangeOfPromoDates,
  getPromoExpirationModalWasShown
} from 'components/ShoppingQuote/BOTProductAndServicesQuoteNew/components/PromotionExpirationModal/selectors'
import {
  setModalWasShown,
  setShowCallStorePromoExpirationNotification,
  setShowInRangePromoExpirationNotification
} from 'components/ShoppingQuote/BOTProductAndServicesQuoteNew/components/PromotionExpirationModal/actions'
import { getPromotionDetailsList } from 'components/ShoppingQuote/BOTProductAndServicesQuoteNew/components/PromotionExpirationModal/selectors.js'
import { getActivePromotionsList } from 'components/QuickAppointmentPage/selectors'
import { PROMOTION_EXPIRATION_MODAL } from 'components/ShoppingQuote/BOTProductAndServicesQuoteNew/constants'
import {
  APPOINTMENT_FORM_NAME,
  ACTIVE_PROMOTIONS_FIELD_NAME
} from 'constant'
import { getAppointmentFormValues } from 'components/AppointmentConfirmationPage/Checkout/selectors'
import { convDate } from 'components/QuickAppointmentPage/ThreeStepper/helpers'
import { isQuickAppointmentPage } from 'helpers/quoteRelatedPages'

const mapStateToProps = state => {
  const cartTotalCount = getQuoteTotalCount(state)
  const pathname = getLocationPathname(state)
  const activePromotionsFromState = getActivePromotionsList(state) || []
  const { activePromotions } = getAppointmentFormValues(state) || {}
  const { isPilotStore } = getUserData(state)

  return {
    pathname,
    quickApptInitialized: getQuickApptInitialized(state),
    storePhoneNumber: getCurrentStorePhoneNumber(state),
    storeNumber: getCurrentStoreNumber(state),
    productsAndServices: getQuoteItemAndServices(state),
    cartTotalCount,
    isCartEmpty: !cartTotalCount,
    appointmentDatesNotInRangeOfPromoDates: getAppointmentDatesNotInRangeOfPromoDates(state),
    calendarStartDate: state.addToQuote.calendarStartDate,
    skusAddedToCart: state.addToQuote.skusAddedToCart,
    promotionDetailsList: getIsOnlineAppointment({ pathname })
      ? activePromotions
      : getPromotionDetailsList(state),
    isPilotStore,
    promoExpirationModalWasShown: getPromoExpirationModalWasShown(state),
    activePromotionsFromState,
    isQuickAppointmentPage: isQuickAppointmentPage(pathname)
  }
}

const withSelectedDate = withState('selectedDate', 'setSelectedDate', {})

const withFormattedDate = withProps(({ selectedDate: { date } = {}, input: { value: { date: committedDate }}}) => ({
  currentDate: date && new Date(date).toISOString(),
  committedDate: committedDate && new Date(committedDate).toISOString()
}))

const withValueSet = withHandlers({
  handleDateSelect: ({
    input: { onChange },
    setSelectedDate,
    pathname,
    validatePromoExpDate,
    promotionDetailsList,
    appointmentDetailsResponseVO: {
      appointmentDateAndSlotsVO = []
    } = {},
    appointmentDatesNotInRangeOfPromoDates,
    show,
    promoExpirationModalWasShown,
    setModalWasShown,
    change,
    activePromotionsFromState
  }) => date => {
    const dates = appointmentDateAndSlotsVO
      .filter(slot => slot.allAvailableTimes.length && slot.startTime !== 'Closed')
      .map(slot => processDate(slot.appointmentDate))
      .sort()

    onChange({ date, time: undefined })
    setSelectedDate({ date, time: undefined })

    if (getIsQuoteSummaryPage({ pathname }) && !appointmentDatesNotInRangeOfPromoDates) {
      validatePromoExpDate({
        firstAvailableDate: dates[0],
        selectedDate: date,
        onChange,
        setSelectedDate,
        promotionDetailsList
      })
    }

    if (getIsOnlineAppointment({ pathname })
      && !appointmentDatesNotInRangeOfPromoDates
      && activePromotionsFromState.length) {
      const parsedSelectedDate = Date.parse(date)
      // Update this date parse for multiple promotions in Phase 2
      const parsedPromoExpirationDate = Date.parse(convDate(activePromotionsFromState[0].promotionExpiryDate))

      if ((parsedSelectedDate > parsedPromoExpirationDate) && !promoExpirationModalWasShown) {
        show(PROMOTION_EXPIRATION_MODAL, {
          appliedPromotions: promotionDetailsList,
          onChange,
          setSelectedDate,
          firstAvailableDate: dates[0]
        })
      }

      if ((parsedSelectedDate <= parsedPromoExpirationDate) && promoExpirationModalWasShown) {
        // TODO Update payload for multiple promotions in Phase 2
        const payload = {
          promotion: activePromotionsFromState[0].promotionId,
          action: 'addPromo'
        }

        rest.api.addOrRemoveServicePromotion(payload)
          .catch(error => {
            console.log(error)
          })

        change(APPOINTMENT_FORM_NAME, ACTIVE_PROMOTIONS_FIELD_NAME, activePromotionsFromState)
        setModalWasShown(false)
      }
    }

    const timesElement = document.getElementById(TIME_SLOTS_TABLE_ID)
    if (timesElement) {
      timesElement.scrollTop = 0
    }
  },
  handleDateTimeSelect: ({ input: { onChange }}) => ({ date, time }) => {
    onChange({ date, time })
  }
})

const withHandleResize = withHandlers({
  handleResize: ({ setIsMobileState }) =>
    debounce(() => setIsMobileState(isMobile()), 500)
})

const withTimeBlocks = withProps(({
  currentDate,
  committedDate,
  dates,
  expanded,
  calendarStartDate,
  skusAddedToCart,
  isPilotStore
}) => {
  if (currentDate) {
    const data = getTimeData(dates, currentDate) || []
    const expandable = data.length > ITEMS_LIMIT
    const timeBlocks = expandable && !expanded ? data.slice(0, ITEMS_LIMIT) : data
    const showNextDay = data.length <= ITEMS_NEXT_DAY_THRESHOLD
    return {
      allTimeBlocks: filterAppointmentSlotsForPilotStore(
        data,
        currentDate,
        calendarStartDate,
        skusAddedToCart,
        isPilotStore
      ),
      timeBlocks: showNextDay ? data : timeBlocks,
      expandable,
      showNextDay,
      isCurrentDateSelected: currentDate === committedDate
    }
  }
})

const withFirstDateSelected = withState('firstDateSelected', 'setFirstDateSelected', false)
const withTomorrowDataSent = withState('tomorrowDataSent', 'setTomorrowDataSent', false)
const withTodaysDataSent = withState('todaysDataSent', 'setTodaysDataSent', false)


const withSetFirstAvailableDate = withHandlers({
  setFirstAvailableDate: ({
    input: { onChange } = {},
    setFirstDateSelected,
    firstDateSelected,
    setSelectedDate
  }) => (appointmentDateAndSlotsVO = []) => {
    const dates = appointmentDateAndSlotsVO
      .filter(slot => slot.allAvailableTimes.length && slot.startTime !== 'Closed')
      .map(slot => processDate(slot.appointmentDate))
      .sort()

    if (dates.length) {
      onChange({ date: dates[0] })
      setSelectedDate({ date: dates[0] })
      !firstDateSelected && setFirstDateSelected(true)
    }
  }
})

const withAnchorProps = withProps(() => ({
  targetId: TIME_SLOTS_ID,
  padding: 20
}))

const withSplitTimeSlots = withProps(({ timeBlocks = []}) => ({
  splitTimeBlocks: shuffle.distributeHorizontally(timeBlocks, 2)
}))

const withLifecycle = lifecycle({
  componentDidMount() {
    const {
      appointmentDetailsResponseVO: {
        selectedAppointmentDate = '',
        selectedAppointmentTime = '',
        appointmentDateAndSlotsVO = []
      } = {},
      input: {
        onChange,
        value: {
          date
        }
      },
      setFirstAvailableDate,
      pathname,
      handleResize,
      setSelectedDate
    } = this.props
    if (!date) {
      if (selectedAppointmentDate && getIsQuotePage({ pathname })) {
        const appointmentDateAndTime = {
          date: processDate(selectedAppointmentDate),
          time: processTime(selectedAppointmentTime)
        }
        onChange(appointmentDateAndTime)
        setSelectedDate(appointmentDateAndTime)
      } else {
        appointmentDateAndSlotsVO && setFirstAvailableDate(appointmentDateAndSlotsVO)
      }
    } else {
      const processedDate = new Date(date).toISOString()
      onChange({ date: processedDate })
      setSelectedDate({ date: processedDate })
    }

    window.addEventListener('resize', handleResize)
  },
  componentDidUpdate({
    appointmentDetailsResponseVO: {
      appointmentDateAndSlotsVO: prevAppointmentDataAndSlotsVO
    } = {},
    quickApptInitialized: prevQuickApptInitialized,
    currentDate: prevCurrentDate,
    productsAndServices: prevProductsAndServices,
    cartTotalCount: prevCartTotalCount,
    skusAddedToCart: prevSkusAddedToCart
  }) {
    const {
      appointmentDetailsResponseVO: {
        appointmentDateAndSlotsVO = []
      } = {},
      quickApptInitialized,
      setFirstAvailableDate,
      currentDate,
      firstDateSelected,
      scrollToAnchorHandler,
      timeBlocks,
      storeNumber: appointmentStoreNumber,
      googleAnalyticDatepickerTomorrowSelected,
      tomorrowDataSent,
      setTomorrowDataSent,
      dates,
      pathname,
      productsAndServices,
      todaysDataSent,
      setTodaysDataSent,
      googleAnalyticDatepickerTodaysSelected,
      cartTotalCount
    } = this.props
    const times = getTimeData(dates, currentDate) || []
    const {
      numberOfSelectableDates,
      numberOfNextDayAppointments,
      nextDayAppointmentOptions
    } = generateAvailableApptTimesPayload(times, appointmentDateAndSlotsVO)

    if (appointmentDateAndSlotsVO &&
      (!isEqual(prevAppointmentDataAndSlotsVO, appointmentDateAndSlotsVO) ||
        (quickApptInitialized && !isEqual(prevQuickApptInitialized, quickApptInitialized)) ||
        (getIsQuotePage({ pathname })
          && wasProductOrServiceAddedOrRemoved(prevProductsAndServices, productsAndServices)) ||
        (!isEqual(prevCurrentDate, currentDate) && !currentDate))) {
      setTimeout(() => setFirstAvailableDate(appointmentDateAndSlotsVO))
    }

    isMobile() && prevCurrentDate !== currentDate && !firstDateSelected &&
      arrayHasValues(timeBlocks) && scrollToAnchorHandler()

    if (tomorrowSelected(currentDate) && !tomorrowDataSent) {
      if (!isEmpty(times)) {
        googleAnalyticDatepickerTomorrowSelected({
          numberOfSelectableDates,
          numberOfNextDayAppointments,
          nextDayAppointmentOptions,
          appointmentStoreNumber
        })

        setTomorrowDataSent(true)
      }
    }

    if (!todaysDataSent && currentDate) {
      googleAnalyticDatepickerTodaysSelected({
        numberOfSelectableDates,
        appointmentStoreNumber,
        numberOfSameDayAppointments: numberOfNextDayAppointments,
        sameDayAppointmentOptions: nextDayAppointmentOptions
      })

      setTodaysDataSent(true)
    }

    if ((cartTotalCount !== prevCartTotalCount)
        && getIsQuoteSummaryPage({ pathname })) {
      setFirstAvailableDate(appointmentDateAndSlotsVO)
    }
  },
  componentWillUnmount() {
    const {
      handleResize,
      setModalWasShown,
      setShowCallStorePromoExpirationNotification,
      setShowInRangePromoExpirationNotification
    } = this.props
    window.removeEventListener('resize', handleResize)
    setModalWasShown(false)
    setShowCallStorePromoExpirationNotification(false)
    setShowInRangePromoExpirationNotification(false)
  }
})

const withRenderCheckoutDateTimeSelector = branch(({ isCheckout }) =>
  isCheckout,
renderComponent(CheckoutDateTimeSelector))

export default compose(
  withState('isMobileState', 'setIsMobileState', isMobile()),
  connect(mapStateToProps, {
    googleAnalyticDatepickerTomorrowSelected,
    googleAnalyticDatepickerTodaysSelected,
    setModalWasShown,
    setShowCallStorePromoExpirationNotification,
    setShowInRangePromoExpirationNotification,
    validatePromoExpDate,
    show,
    change
  }),
  withSelectedDate,
  withHandleResize,
  withFormattedDate,
  withValueSet,
  toggle,
  withTimeBlocks,
  withSplitTimeSlots,
  withFirstDateSelected,
  withTomorrowDataSent,
  withTodaysDataSent,
  withSetFirstAvailableDate,
  withAnchorProps,
  withScrollToAnchor,
  withLifecycle,
  withRenderCheckoutDateTimeSelector
)(DateTimeSelector)
